import { RESOURCE_VIEW_COLUMNS } from "./SchedulerView/resourceViewColumnsData.jsx";
import {
  replicateEventsHorizontally,
  scheduleEventRenderer, snapEventToTickOnDrop
} from "../utils/schedulerUtils.jsx";
import SwivuCellEditor from "../../bryntum-lib/widget/fields/SwivuCellEditor.jsx";
import {
  cellMenuItemCollapseAllRows, cellMenuItemCollapseRow,
  cellMenuItemEditEntity,
  cellMenuItemRemoveRow,
  cellMenuItemSettingsEditor,
  cellMenuItemToggleArchiveRow,
  entityCategorySelector,
  entitySendBoardAlert,
  entityStatusSelector,
  entityTagsSelector,
  entityTeamSelector
} from "../utils/gridCellMenuUtils.jsx";
import { cellAvatarTooltipConfig, eventTooltipRenderer } from "../utils/utils.jsx";
import EventPushByMenu from "../../bryntum-lib/editors/eventMenuPopup/EventPushByMenu.jsx";
import { RESOURCE_VIEW_FILTER_FIELD_CONFIG } from "./PlanView/planResourceViewColumnsData.jsx";
import SwivuCalendarHelper from "../utils/calendarUtils.jsx";
import { SWIVU_GROUP_CONTEXT_MENU_SELECTOR, TIMELINE_CONG_KEYS } from "./OtherKeys.jsx";
import { gridHeaderMenuProcessItems } from "../utils/otherUtils.jsx";
import { userPreferences } from "../../react/context/bryntumContext.jsx";
import { StringHelper, Tooltip } from "@bryntum/schedulerpro";
import { PERSISTENCE_STATE_KEYS } from "./PersistenceStateKeys.jsx";
import { PLUGINS_KEYS } from "./PluginKeys.jsx";

export const SCHEDULER_CONFIG = {
  columns: RESOURCE_VIEW_COLUMNS,
  createEventOnDblClick: false,
  // Clicking header sorts events now, disabling zooming to not overload header with interactions
  zoomOnTimeAxisDoubleClick: false,
  zoomOnMouseWheel: false,
  preserveScrollOnDatasetChange: true,
  cls: "swivu-scheduler",
  // If true scheduler will adjust height according to content. We need it to take maximum space that parent node gives it
  autoHeight: false,
  infiniteScroll: true,
  // eventStyle: "border",
  resourceImagePath: "/id_photos",
  viewPreset: "dayAndWeek",
  subGridConfigs: {
    left: {
      weight: 10,
      width: "12.5%"
    },
    locked: {
      weight: 20,
      width: "12.5%"
    },
    normal: {
      weight: 30,
      flex: 1
    }
  },
  startDate: new Date(),
  eventRenderer: scheduleEventRenderer,
  filterFeature: {
    pickerConfig: {
      fields: RESOURCE_VIEW_FILTER_FIELD_CONFIG
    },
    closeEmptyPopup: true
  },
  dependenciesFeature: false,
  resourceEditFeature: false,
  timeRangesFeature: {
    currentDateFormat: "h:mm A",
    showCurrentTimeLine: true,
  },
  sortFeature: {
    multiSort: false
  },
  taskEditFeature: false,
  eventTooltipFeature: {
    template: ({ eventRecord, startDate, endDate, startText, endText }) => {
      const taskRecord = eventRecord.task;
      if (taskRecord) {
        const resources = taskRecord.events.filter((event) => {
          return event.startDate.toString() === startDate.toString() && event.endDate.toString() === endDate.toString();
        }).map((event) => event.resource);
        return eventTooltipRenderer(eventRecord.name, startText, endText, resources);
      }
    },
    listeners: {
      beforeShow({ source }) {
        // Based on the value of custom property tooltip is rendered
        // Custom property is added/toggle from eventMouseOver listener, See Line: 163.
        return source.eventRecord._swivuShowTooltip;
      },
    },
  },
  scheduleTooltipFeature: false,
  statefulEvents: [
    "subGridCollapse",
    "subGridExpand",
    "horizontalScroll",
    "stateChange",
    "scroll",
    "swivuResourceStoreFilter",
    "swivuResourceStoreSort",
    "swivuResourceStoreGroup",
  ],
  eventDragCreateFeature: true,
  eventDragFeature: {
    showTooltip: false,
    // when hiding non-working times snap to position does not work causing the events to freeze
    // snapToPosition: snapEventToTickOnDrag,
    copyMode: "event",
    copyKey: "ALT"
  },
  eventResizeFeature: {
    showExactResizePosition: true
  },
  // there's a custom plugin called `SwivuSchedulerMenuPlugin` for added features displaying on Scheduler
  scheduleMenuFeature: true,
  eventMenuFeature: {
    items: {
      // All events use single assignment, so naturally this option duplicates delete
      unassignEvent: false,
      splitEvent: false,
      pushEvent: {
        icon: "fa-solid fa-right-to-line",
        text: "Push event",
        menu: {
          items : {
            pushOneDay: {
              text: "Push 1 day",
              onItem({ source: scheduler, menu, resourceRecord, eventRecord }) {
                const point = scheduler.features.eventMenu.menuContext.point;
                const clickedDate = scheduler.getDateFromCoordinate(point[0], "floor", false);
                menu.owner.pushEventsBy(clickedDate, resourceRecord, undefined, undefined, eventRecord, scheduler);
              }
            },
            pushCustom: {
              text: "Custom...",
              onItem({ source: scheduler, menu, resourceRecord, eventRecord }) {
                const point = scheduler.features.eventMenu.menuContext.point;
                const clickedDate = scheduler.getDateFromCoordinate(point[0], "floor", false);

                const popup = new EventPushByMenu({
                  listeners: {
                    save({ unit, amount }) {
                      menu.owner.pushEventsBy(clickedDate, resourceRecord, unit, amount, eventRecord, scheduler);
                    }
                  }
                });
                popup.show();
              }
            }
          }
        }
      }
    }
  },
  headerMenuFeature: {
    processItems: gridHeaderMenuProcessItems("leftRegion", "lockedRegion")
  },
  // This is enabled as we had a requirement a display focused cell on time axis view. see also scheduleMouseMove function below
  timeSpanHighlightFeature: true,
  timeAxisHeaderMenuFeature: {
    items: {
      dateRange: false,
      currentTimeLine: true,
      pushEvent: {
        icon: "fa-solid fa-right-to-line",
        text: "Push events",
        menu: {
          items : {
            pushOneDay: {
              text: "Push 1 day",
              onItem({ source: scheduler, menu }) {
                const point = scheduler.features.timeAxisHeaderMenu.menuContext.point;
                const clickedDate = scheduler.getDateFromCoordinate(point[0], "floor", false);

                menu.owner.pushEventsBy(clickedDate, null, undefined, undefined, null, scheduler);
              }
            },
            pushCustom: {
              text: "Custom...",
              onItem({ source: scheduler, menu }) {
                const point = scheduler.features.timeAxisHeaderMenu.menuContext.point;
                const clickedDate = scheduler.getDateFromCoordinate(point[0], "floor", false);

                const popup = new EventPushByMenu({
                  listeners: {
                    save({ unit, amount }) {
                      menu.owner.pushEventsBy(clickedDate, null, unit, amount, null, scheduler);
                    }
                  }
                });
                popup.show();
              }
            }
          }
        }
      },
      eventsFilter: true,
      copyColumn: {
        icon: "fa-solid fa-copy",
        text: "Copy Column",
        onItem: "up.onClickSwivuTimelineCopy",
      },
      pasteColumn: {
        icon: "fa-solid fa-paste",
        text: "Paste Column",
        onItem: "up.onClickSwivuTimelinePaste",
      }
    },
    processItems: "up.onProcessSwivuTimeAxisHeaderMenu"
  },
  // This listener is required to update event start date to pin it to custom date. Normally that is done via
  // timeResolution config for the view preset: https://bryntum.com/docs/gantt/api/Scheduler/preset/ViewPreset#field-timeResolution
  // But that has some side effects like date in the tooltip. Also we'd need to redefine zoom levels.
  onEventDrop: snapEventToTickOnDrop,
  timeAxis: {
    listeners: {
      reconfigure(event) {
        SwivuCalendarHelper.reconfigureVisualNonWorkingDaysFromCalendar(
          event.source.owner
        );
      }
    }
  },
  listeners: {
    beforeEventResizeFinalize({ context, event, source }) {
      // Creates copies of the event if resizing with ALT/OPTION key
      // https://github.com/Swivu/swivu-client/issues/766
      replicateEventsHorizontally.call(this, { context, event, source });
    },
    eventMouseOver(e) {
      const { event, eventRecord } = e;
      // This listener toggles a custom property based on whether the shift key is pressed or not, whenever mouse over event is triggered
      eventRecord._swivuShowTooltip = event.shiftKey;
      if (userPreferences.getSetting(PERSISTENCE_STATE_KEYS.hoverTaskEventHighlight) === "true") {
        e.scheduler.features.timeSpanHighlight.unhighlightTimeSpans();
        e.scheduler.selectEvent(e.assignmentRecord.event, false)
      }
    },
    mouseOver(e) {
      // There are situations where the highlight element stuck into the time axis view even after moving out the mouse from schedule
      if (StringHelper.safeJsonParse(userPreferences?.getSetting(PERSISTENCE_STATE_KEYS.hoverGridCellHighlight, true))) {
        e.source.features.timeSpanHighlight.unhighlightTimeSpans();
        if (e.source.swivuScheduleIgnoreTooltipOnHover) {
          e.source.swivuScheduleIgnoreTooltipOnHover.hide();
        }
      }
    },
    scheduleMouseMove(e) {
      if (!e.resourceRecord.isGroupHeader && userPreferences && StringHelper.safeJsonParse(userPreferences.getSetting(PERSISTENCE_STATE_KEYS.hoverGridCellHighlight, true))) {
        const calendar = e.scheduler.project.calendarManagerStore.getById("weekends");
        const timelineConfigState = e.scheduler.features[PLUGINS_KEYS.swivuTimelineConfigurator].state;
        const skipNonWorkingDayDuringReplicating = timelineConfigState[TIMELINE_CONG_KEYS.skipNonWorkingDayDuringReplicating];
        const padding = e.scheduler.resourceMargin;
        const highlightData = {
          startDate: e.tickStartDate,
          endDate: e.tickEndDate,
          cls: "swivu-timespan-cell-highlight",
          resourceRecord: e.resourceRecord,
          // NOTE: by default highlightTimeSpan function doesn't support padding object we have developed an override called RectangleOverride in which we cater this
          padding: {
            top: padding - 5,
            bottom: padding - 5,
            left: -5,
            right: -5,
          }
        }
        // Todo: Find reason.... I am not sure why this statement is here... as user focusing event and then moving away mouse cause the event to be unselected
        // leaving this here for now...
        // e.scheduler.clearEventSelection();

        if (skipNonWorkingDayDuringReplicating && !calendar.isWorkingTime(highlightData.startDate, highlightData.endDate)) {
          if (!e.scheduler.swivuScheduleIgnoreTooltipOnHover) {
            // show this tooltip only on hovering over non-working time cell when ignore non-working is enabled
            e.scheduler.swivuScheduleIgnoreTooltipOnHover = new Tooltip({
              forElement: e.scheduler.timeAxisSubGridElement,
              // copied this selector from ScheduleTooltip.js feature code file
              forSelector: ".b-schedulerbase:not(.b-dragging-event,.b-dragcreating,.b-creating-dependency) .b-grid-body-container:not(.b-scrolling) .b-timeline-subgrid:not(.b-scrolling) > :not(.b-sch-foreground-canvas,.b-group-footer,.b-group-row) *:not(.b-scroll-button-wrap *,.b-sch-dependency)",
              html: "Ignoring Non-Working Time",
              allowOver: false,
              trackMouse: true,
              autoShow: false,
              showOnHover: false,
            });
          }

          highlightData.cls = `${highlightData.cls} ignored`;
          e.scheduler.features.timeSpanHighlight.highlightTimeSpan(highlightData);
          e.scheduler.timeAxisSubGridElement.style.cursor = "no-drop";
          e.scheduler.swivuScheduleIgnoreTooltipOnHover.show();
        } else {
          if (e.scheduler.swivuScheduleIgnoreTooltipOnHover) {
            e.scheduler.timeAxisSubGridElement.style.cursor = "";
            e.scheduler.swivuScheduleIgnoreTooltipOnHover.hide();
          }
          // Always use minor tick alignment - use the existing tick data
          // highlightData already has the correct startDate and endDate from e.tickStartDate and e.tickEndDate
          e.scheduler.features.timeSpanHighlight.highlightTimeSpan(highlightData);
        }
      } else {
        e.scheduler.features.timeSpanHighlight.unhighlightTimeSpans();
      }
    },
  },
  rowCopyPasteFeature: false,
  rowReorderFeature: true,
  cellEditFeature: { editorClass: SwivuCellEditor },
  cellTooltipFeature: {
    ...cellAvatarTooltipConfig,
    tooltipRenderer(e) {
      if (e.column?.swivuTooltip) {
        return e.column?.swivuTooltip(e);
      }
    }
  },
  cellMenuFeature: {
    clickTriggerSelector: SWIVU_GROUP_CONTEXT_MENU_SELECTOR,
    items: {
      cut: false,
      copy: false,
      paste: false,
      filterStringEquals: false,
      filterDisable: false,
      removeRow: cellMenuItemRemoveRow(),
      archive: cellMenuItemToggleArchiveRow(),
      filterMenu: false,
      // Resource's View Agenda is temporarily disabled
      // viewAgenda: cellMenuItemViewAgenda(),
      editEntity : cellMenuItemEditEntity({ text: "Edit Resource" }),
      collapseRow: cellMenuItemCollapseRow(),
      collapseAllRows: cellMenuItemCollapseAllRows(),
      settingsEditor: cellMenuItemSettingsEditor(),
      entityStatusSelector: entityStatusSelector(),
      entityCategorySelector: entityCategorySelector(),
      entityTagsSelector: entityTagsSelector(),
      entityTeamSelector: entityTeamSelector(),
      entitySendBoardAlert: entitySendBoardAlert(),
    },
  },
  syncMask: false
};
